'use client'

/**
 * Authentication Context Provider
 *
 * The browser does not hold tokens. Session state is sourced from the
 * server-side `/api/auth/me` endpoint, which reads HttpOnly session cookies.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import type { AuthState, User } from './types'
import type { UserProfile } from '@/lib/services/profileService'
import { getMyInvitations } from '@/lib/services/teamService'

interface AuthContextValue extends AuthState {
  login: (returnUrl?: string) => void
  logout: () => void
  refreshUser: () => Promise<void>
  refreshInvitations: () => Promise<void>
}

interface MeResponse {
  profile: UserProfile
  roles: string[]
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function profileToUser(profile: UserProfile, roles: string[]): User {
  return {
    id: profile.id,
    email: profile.email ?? null,
    name: profile.name,
    username: profile.username,
    avatarUrl: profile.avatarUrl ?? null,
    bio: profile.bio,
    socialLinks: profile.socialLinks,
    emailVerified: true,
    roles,
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt,
  }
}

async function fetchSession(): Promise<MeResponse | null> {
  const res = await fetch('/api/auth/me', {
    credentials: 'include',
    cache: 'no-store',
  })
  if (res.status === 401) return null
  if (!res.ok) throw new Error(`Failed to load session: ${res.status}`)
  return (await res.json()) as MeResponse
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    pendingInvitationsCount: 0,
  })

  const loadUser = useCallback(async () => {
    try {
      const session = await fetchSession()
      if (!session) {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          pendingInvitationsCount: 0,
        })
        return
      }
      setState({
        user: profileToUser(session.profile, session.roles),
        isAuthenticated: true,
        isLoading: false,
        error: null,
        pendingInvitationsCount: 0,
      })
    } catch (error) {
      console.error('Failed to load session:', error)
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'unknown_error',
        pendingInvitationsCount: 0,
      })
    }
  }, [])

  useEffect(() => {
    void loadUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback((returnUrl?: string) => {
    const target = returnUrl || window.location.pathname + window.location.search
    const safe = target.startsWith('/') && !target.startsWith('//') ? target : '/'
    window.location.href = `/api/auth/login?returnUrl=${encodeURIComponent(safe)}`
  }, [])

  const logout = useCallback(() => {
    // Hold the loading state so layouts don't flash a logged-out UI before redirect.
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      pendingInvitationsCount: 0,
    })
    window.location.href = '/api/auth/logout'
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const session = await fetchSession()
      if (!session) {
        setState((prev) => ({ ...prev, user: null, isAuthenticated: false }))
        return
      }
      setState((prev) => ({
        ...prev,
        user: profileToUser(session.profile, session.roles),
        isAuthenticated: true,
      }))
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }, [])

  const refreshInvitations = useCallback(async () => {
    try {
      const result = await getMyInvitations()
      if (result.success && result.data) {
        setState((prev) => ({
          ...prev,
          pendingInvitationsCount: result.data!.invitations.length,
        }))
      }
    } catch (error) {
      console.error('Failed to refresh invitations:', error)
    }
  }, [])

  /**
   * Periodic user profile + invitations refresh.
   * Token rotation is handled transparently by the BFF proxy on each call,
   * so we no longer need a client-side refresh timer.
   */
  useEffect(() => {
    if (!state.isAuthenticated) return

    void refreshInvitations()

    const interval = setInterval(() => {
      void refreshUser()
      void refreshInvitations()
    }, 300000)

    return () => clearInterval(interval)
  }, [state.isAuthenticated, refreshUser, refreshInvitations])

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshUser,
    refreshInvitations,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function useUser(): User {
  const { user, isAuthenticated } = useAuth()
  if (!isAuthenticated || !user) {
    throw new Error('User is not authenticated')
  }
  return user
}

export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth()
  return isAuthenticated
}
